import { totalsByCurrency, type CurrencyTotal } from '../../platform/money/totals'
import { daysBetween, passbookSection, sectionOf, type LocalDate, type PassbookKey, type SectionKey } from './recurringRules'
import type { Bill, Entry } from './recurringWire'
import { entryAmountCents } from './useRecurring'

export type { SectionKey }

/** P2's order on screen. */
export const SECTION_ORDER: readonly SectionKey[] = ['overdue', 'upcoming', 'skipped', 'paid']

export type Section = {
  key: SectionKey
  /** Every entry in the section, inflows included: an inflow is listed with "Income". */
  entries: Entry[]
  /** Outflows only (D6). */
  count: number
  /** Outflows only, one per currency (D21, S12). More than one means no single figure exists. */
  totals: CurrencyTotal[]
}

export function monthSections(entries: Entry[], today: LocalDate): Record<SectionKey, Section> {
  const out = {} as Record<SectionKey, Section>
  for (const key of SECTION_ORDER) {
    const inSection = entries
      .filter((e) => sectionOf(e, today) === key)
      .sort((a, b) => (a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : 0))
    const outflows = inSection.filter((e) => !e.bill.isInflow)
    out[key] = {
      key,
      entries: inSection,
      count: outflows.length,
      totals: totalsByCurrency(outflows, entryAmountCents, (e) => e.bill.currency),
    }
  }
  return out
}

/**
 * The month's sections narrowed to one day (a day click on the grid).
 *
 * A FILTER over sections the caller already classified, never a second
 * classification: no `sectionOf` here, so the grid, the list and the narrowed
 * list cannot disagree about a row (Ruling 30). Totals are recomputed from the
 * day's visible OUTFLOWS with the month's own `totalsByCurrency`, so they stay
 * per currency and inflows stay out of them (D6, D21).
 */
export function narrowSections(sections: Record<SectionKey, Section>, dueDate: LocalDate): Record<SectionKey, Section> {
  const out = {} as Record<SectionKey, Section>
  for (const key of SECTION_ORDER) {
    const entries = sections[key].entries.filter((e) => e.dueDate === dueDate)
    const outflows = entries.filter((e) => !e.bill.isInflow)
    out[key] = {
      key,
      entries,
      count: outflows.length,
      totals: totalsByCurrency(outflows, entryAmountCents, (e) => e.bill.currency),
    }
  }
  return out
}

/** Days late of the oldest overdue OUTFLOW, or null when nothing is overdue. */
export function oldestOverdue(overdue: Section, today: LocalDate): number | null {
  const oldest = overdue.entries.find((e) => !e.bill.isInflow)
  return oldest ? daysBetween(oldest.dueDate, today) : null
}

/** S11: distinct bills whose bank needs the user, counted once however often they recur (S5). */
export function reconnectBillCount(entries: Entry[]): number {
  return new Set(
    entries
      .filter((e) => e.bill.accountStatus === 'reauth_required' || e.bill.accountStatus === 'disconnected')
      .map((e) => e.bill.id),
  ).size
}

const MONTH_NAME = new Intl.DateTimeFormat('en-CA', { month: 'long', timeZone: 'UTC' })

/** "July". Formatted at UTC from the month's own number, so no zone can shift it. */
export function monthName(ym: { year: number; month: number }): string {
  return MONTH_NAME.format(Date.UTC(ym.year, ym.month - 1, 1))
}

/** The most frequent currency among `items`, ties to the first seen; undefined when empty. */
function mostCommon(items: readonly string[]): string | undefined {
  const counts = new Map<string, number>()
  for (const c of items) counts.set(c, (counts.get(c) ?? 0) + 1)
  let best: string | undefined
  for (const [c, n] of counts) if (best === undefined || n > (counts.get(best) ?? 0)) best = c
  return best
}

/**
 * The currency a ZERO tile is written in. A true zero still needs a symbol, and
 * the household's own money is the honest one: the month's outflows first, then
 * the active outflow bills, then any bill. The list is never empty here (no
 * bills is the empty state), so there is no hard-coded default to fall back on.
 */
export function zeroCurrency(entries: readonly Entry[], bills: readonly Bill[]): string {
  return (
    mostCommon(entries.filter((e) => !e.bill.isInflow).map((e) => e.bill.currency)) ??
    mostCommon(bills.filter((b) => b.isActive && !b.isInflow).map((b) => b.currency)) ??
    bills[0]!.currency
  )
}

/** The Passbook's order on screen. */
export const PASSBOOK_ORDER: readonly PassbookKey[] = ['overdue', 'upcoming', 'paid', 'paused', 'income']

export type PassbookSection = {
  key: PassbookKey
  /** One entry per stream (S5). */
  entries: Entry[]
  /** Outflows only (D6), one per currency (S12). Empty for Income, which shows its count instead. */
  totals: CurrencyTotal[]
}

/**
 * The ONE place a stream becomes a row's `Entry`, and the one place the
 * synthetic status is set. The Passbook has no per-occurrence periods, so the
 * entry stands for the stream's next period (`nextDue`):
 *
 * - Status 'upcoming' lets `canMarkPaid` / `canSkip` apply: they are true for
 *   exactly the period the server acts on, `nextDue`. A paused bill still owes
 *   nothing (they check `isPaused`).
 * - A Paid-this-month stream is status 'paid' (Ruling 49): this cycle is
 *   settled, so neither Mark paid nor Skip is offered. Its amount is what was
 *   last paid (`lastCents`), falling back to the expected amount only when the
 *   wire omitted it.
 * - `dueDate` is `nextDue`, else `lastDate`, else ''. It is never shown when
 *   `nextDue` is null (the subline omits dates then); '' sorts first.
 */
export function streamEntry(bill: Bill, key: PassbookKey): Entry {
  const paid = key === 'paid'
  return {
    bill,
    dueDate: bill.nextDue ?? bill.lastDate ?? '',
    status: paid ? 'paid' : 'upcoming',
    payment: null,
    expectedCents: paid ? (bill.lastCents ?? bill.expectedCents) : bill.expectedCents,
  }
}

export function passbookSections(bills: readonly Bill[], today: LocalDate): Record<PassbookKey, PassbookSection> {
  const out = Object.fromEntries(PASSBOOK_ORDER.map((k) => [k, { key: k, entries: [], totals: [] }])) as unknown as Record<
    PassbookKey,
    PassbookSection
  >
  for (const b of bills) {
    const k = passbookSection(b, today)
    if (k !== null) out[k].entries.push(streamEntry(b, k))
  }
  for (const k of PASSBOOK_ORDER) {
    const sec = out[k]
    sec.entries.sort((a, b) => (a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : a.bill.name.localeCompare(b.bill.name)))
    sec.totals =
      k === 'income'
        ? []
        : totalsByCurrency(
            sec.entries.filter((e) => !e.bill.isInflow),
            entryAmountCents,
            (e) => e.bill.currency,
          )
  }
  return out
}
